import React, { useContext, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { MapActions, MapContext } from "context/map";
import { getStackOrdersById, getZoningOrderById } from "api/Orders";
import { toast } from "react-toastify";
import { UserContext } from "context/user";
import FarmFilter from "../FarmFilter";
import Legend from "../Legend";
import Catalog from "../Catalog";
import RasterLoading from "../RasterLoading";
import Tutorial from "../Tutorial";

const zoneFillColors = {
  2: ["match", ["get", "zone"], 1, "#ffe75e", 2, "#34ffa1", "#FFFFFF"],
  3: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#b9ff55",
    3,
    "#1260ff",
    "#FFFFFF",
  ],
  4: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#fcff54",
    3,
    "#54f174",
    4,
    "#1260ff",
    "#FFFFFF",
  ],
  5: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#ffe75e",
    3,
    "#b9ff55",
    4,
    "#34ffa1",
    5,
    "#1260ff",
    "#FFFFFF",
  ],
};

const ViewFarm = ({ lastSelectedSourceRef }) => {
  const { token, profile } = useContext(UserContext);
  const location = useLocation();
  const zoneParam = new URLSearchParams(location.search).get("zone");
  const stackParam = new URLSearchParams(location.search).get("stack");
  const { state, dispatch } = useContext(MapContext);
  const {
    mode,
    catalog,
    selectedDate,
    selectedAlgorithm,
    map,
    selectedFields,
    selectedZone,
    selectedLayer,
    fields,
  } = state;
  const [zoneFields, setZoneFields] = useState(null);

  const fetchZoneOrderById = useCallback(
    async (id) => {
      try {
        const response = await getZoningOrderById(id, token);
        setZoneFields(response?.fields);
      } catch (e) {
        toast.error(e);
      }
    },
    [token]
  );

  useEffect(() => {
    if (zoneParam) {
      fetchZoneOrderById(zoneParam);
    }
  }, [fetchZoneOrderById, zoneParam]);

  const fetchStackOrderById = useCallback(
    async (id) => {
      try {
        const response = await getStackOrdersById(id, token);
        setZoneFields(response?.fields);
      } catch (e) {
        toast.error(e);
      }
    },
    [token]
  );

  useEffect(() => {
    if (stackParam) {
      fetchStackOrderById(stackParam);
    }
  }, [stackParam, fetchStackOrderById]);

  useEffect(() => {
    if (zoneFields?.length > 0) {
      const currentSelectedFields = fields.filter((field) =>
        zoneFields?.includes(parseInt(field.id, 10))
      );
      const multiSelectEnabled = profile.feature_flags.find(
        (item) =>
          item.feature_flag === "field:multi_select" && item.enabled === true
      );
      if (currentSelectedFields.length > 0) {
        dispatch({
          type: MapActions.REMOVE_SELECTED_FIELDS,
        });
        currentSelectedFields.map((field) =>
          dispatch({
            type: MapActions.SET_SELECTED_FIELDS,
            value: { fieldId: field.id, multiSelectEnabled },
          })
        );
      }
    }
  }, [zoneFields, dispatch, profile, fields]);

  useEffect(() => {
    if (zoneParam) {
      dispatch({
        type: MapActions.SET_SELECTED_LAYER,
        value: "zoning",
      });
    }
  }, [zoneParam, dispatch]);

  useEffect(() => {
    if (stackParam) {
      dispatch({
        type: MapActions.SET_SELECTED_LAYER,
        value: "stack",
      });
    }
  }, [stackParam, dispatch]);

  useEffect(() => {
    if (zoneParam && selectedLayer === "zoning" && catalog.length) {
      dispatch({
        type: MapActions.SET_SELECTED_ZONE,
        value: parseInt(zoneParam, 10),
      });
    }
  }, [zoneParam, selectedLayer, catalog.length, dispatch]);

  useEffect(() => {
    if (stackParam && selectedLayer === "stack" && catalog.length) {
      dispatch({
        type: MapActions.SET_SELECTED_ZONE,
        value: parseInt(stackParam, 10),
      });
    }
  }, [stackParam, selectedLayer, catalog.length, dispatch]);

  useEffect(() => {
    selectedFields.forEach((fieldId) => {
      const fieldFill = map.getLayer(`fill-${fieldId}`);

      if (!fieldFill) {
        map.addLayer({
          id: `fill-${fieldId}`,
          type: "fill",
          source: `source-${fieldId}`,
          paint: {
            "fill-color": [
              "case",
              ["boolean", ["feature-state", "active"], false],
              "#09BA51",
              "#FFFFFF",
            ],
            "fill-opacity": [
              "case",
              ["boolean", ["feature-state", "tiled"], false],
              0,
              ["boolean", ["feature-state", "active"], false],
              0.2,
              ["boolean", ["feature-state", "hover"], false],
              0.5,
              0,
            ],
          },
        });
      }
    });
  }, [map, selectedDate, selectedFields]);

  useEffect(() => {
    if (!selectedZone) return;

    const zoneSource = map.getSource("zones");
    const zoneFill = map.getLayer("zones");
    const zoneOutline = map.getLayer("zones-outline");
    const catalogData = catalog.find((item) => item.id === selectedZone);
    if (catalogData === undefined) {
      dispatch({
        type: MapActions.SET_SELECTED_ZONE,
        value: null,
      });
      return;
    }
    const zoneJson = JSON.parse(catalogData.zones);
    const zoneFeatures = selectedFields.length
      ? zoneJson.features.filter((item) =>
          selectedFields.includes(item.properties.field_id.toString())
        )
      : zoneJson.features;
    const numberOfZones = zoneJson?.rates
      ? Object.keys(zoneJson.rates).length
      : 5;

    if (zoneSource) {
      zoneSource.setData({
        type: "FeatureCollection",
        features: zoneFeatures,
      });
    } else {
      map.addSource("zones", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: zoneJson.features,
        },
      });
    }

    if (!zoneFill) {
      map.addLayer({
        id: "zones",
        type: "fill",
        source: "zones",
        paint: {
          "fill-opacity": 1,
        },
      });
    }

    if (!zoneOutline) {
      map.addLayer({
        id: "zones-outline",
        type: "line",
        source: "zones",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#6E6E6E",
          "line-width": 0.6,
        },
      });
    }

    map.setPaintProperty("zones", "fill-color", zoneFillColors[numberOfZones]);
  });

  const resetSelectedZone = useCallback(() => {
    if (selectedFields || selectedLayer) {
      const zoneSource = map.getSource("zones");

      if (zoneSource) {
        map.getSource("zones").setData({
          type: "FeatureCollection",
          features: [],
        });
      }

      dispatch({
        type: MapActions.SET_SELECTED_ZONE,
        value: null,
      });
    }
  }, [selectedLayer, selectedFields, dispatch, map]);

  useEffect(() => {
    resetSelectedZone();
  }, [resetSelectedZone]);

  const checkTutorialMode = useCallback(() => {
    const skippedPopoverTutorial = localStorage.getItem(
      "skip-popover-tutorial"
    );

    if (skippedPopoverTutorial !== "1") {
      dispatch({
        type: MapActions.SET_TUTORIAL_MODE,
        value: true,
      });
    }
  }, [dispatch]);

  const setAlgorithms = useCallback(() => {
    const algorithms = catalog
      .find(
        (item) => item.date === selectedDate || item.start_date === selectedDate
      )
      .items.map((item) => item.algorithm);
    const newSelectedAlgorithm = algorithms.includes(selectedAlgorithm)
      ? selectedAlgorithm
      : "NDVI";

    dispatch({
      type: MapActions.SET_ALGORITHMS,
      value: { algorithms, selectedAlgorithm: newSelectedAlgorithm },
    });
  }, [catalog, dispatch, selectedAlgorithm, selectedDate]);

  useEffect(() => {
    checkTutorialMode();
  }, [checkTutorialMode]);

  useEffect(() => {
    if (selectedDate) {
      setAlgorithms();
    }
  }, [selectedDate, setAlgorithms]);

  useEffect(() => {
    if (location && location.state?.draw) {
      dispatch({ type: MapActions.SET_MANAGE_MODE });
    }
  }, [dispatch, location]);

  useEffect(() => {
    if (map) {
      map.on("load", () => {
        const fieldId = location.state?.selectedField;

        if (location && location.state?.view && fieldId) {
          dispatch({
            type: MapActions.SET_SELECTED_FIELDS,
            value: { fieldId: `${fieldId}` },
          });

          location.state = {};
          window.history.replaceState({}, document.title);
        }

        if (location && location.state?.edit && fieldId) {
          dispatch({ type: MapActions.SET_MANAGE_MODE });
          dispatch({
            type: MapActions.SET_SELECTED_FIELDS,
            value: { fieldId: `${fieldId}` },
          });

          location.state = {};
          window.history.replaceState({}, document.title);
        }
      });
    }
  }, [dispatch, location, map]);

  if (mode !== "view") return null;

  return (
    <>
      <FarmFilter lastSelectedSourceRef={lastSelectedSourceRef} />
      <Legend />
      <Catalog />
      <RasterLoading />
      <Tutorial />
    </>
  );
};

ViewFarm.defaultProps = {
  lastSelectedSourceRef: { current: null },
};

ViewFarm.propTypes = {
  lastSelectedSourceRef: PropTypes.shape({
    current: PropTypes.string,
  }),
};

export default ViewFarm;
